import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Container, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { UPDATE_GROUP_VERSION_OGMP_CATEGORY_MUTATION } from "../../../../graphql/mutations";

const EditVersionCategoryModal = ({
  show,
  handleClose,
  category,
  parentName,
  versionCategory,
  groupVersionId,
  technologies = [],
  getTechnologyName,
  viewOnly = false,
  handleEnvChange,
  env,
  currentTenant,
  tenantName
}) => {
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState({});

  const [formData, setFormData] = useState({
    ogmpCategoryId: "",
    dataSource: "",
    frequency: "",
    l4Method: "",
    measurementTechnologyId: "",
    measurementName: "",
    sampleSize: "",
    sampleSizeUnit: "",
    uncertaintyFraction: ""
  });

  useEffect(() => {
    if (category && versionCategory) {
      setFormData({
        ogmpCategoryId: category.id || "",
        dataSource: versionCategory.dataSource || "",
        frequency: versionCategory.frequency || "",
        l4Method: versionCategory.l4Method || "",
        measurementTechnologyId: versionCategory.measurementTechnologyId || "",
        measurementName: getTechnologyName(versionCategory.measurementTechnologyId),
        sampleSize: versionCategory.sampleSize || "",
        sampleSizeUnit: versionCategory.sampleSizeUnit || "",
        uncertaintyFraction: versionCategory.uncertaintyFraction || ""
      });
      setValidationErrors({});
    }
  }, [category, versionCategory, getTechnologyName]);

  const [updateVersionCategory, { loading: mutationLoading }] = useMutation(UPDATE_GROUP_VERSION_OGMP_CATEGORY_MUTATION);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    if (value === "" || (!isNaN(value) && value.trim() !== "")) {
      setFormData({
        ...formData,
        [name]: value
      });

      if (validationErrors[name]) {
        setValidationErrors((prev) => ({
          ...prev,
          [name]: ""
        }));
      }
    }
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData({
      ...formData,
      [name]: selectedOption ? selectedOption.value : ""
    });

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.frequency) {
      errors.frequency = "Frequency is required";
    }

    if (formData.uncertaintyFraction && (parseFloat(formData.uncertaintyFraction) < 0 || parseFloat(formData.uncertaintyFraction) > 1)) {
      errors.uncertaintyFraction = "Uncertainty Fraction must be between 0 and 1";
    }

    if (formData.sampleSize && !formData.sampleSizeUnit) {
      errors.sampleSizeUnit = "Unit is required when the Sample Size is provided";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onModalClose = () => {
    handleClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const sampleSizeValue = formData.sampleSize ? parseFloat(formData.sampleSize) : null;
      const sampleSizeUnit = formData.sampleSize ? formData.sampleSizeUnit : null;

      const { data } = await updateVersionCategory({
        variables: {
          versionCategoryInput: {
            groupVersionId: groupVersionId,
            ogmpCategoryId: formData?.ogmpCategoryId,
            dataSource: formData?.dataSource,
            frequency: formData?.frequency,
            l4Method: formData?.l4Method,
            measurementTechnologyId: formData?.measurementTechnologyId,
            sampleSize: sampleSizeValue,
            sampleSizeUnit: sampleSizeUnit,
            uncertaintyFraction: formData.uncertaintyFraction ? parseFloat(formData.uncertaintyFraction) : null
          }
        }
      });

      if (data?.updateGroupVersionOGMPCategory) {
        onModalClose();
        setTimeout(() => {
          toast.success(`Version OGMP Category: '${category?.name}' updated successfully.`);
          navigate("/version-categories");
          handleEnvChange(env, currentTenant, tenantName, groupVersionId);
        }, 200);
      }
    } catch (error) {
      onModalClose();
      setTimeout(() => {
        toast.error(`Failed to update version ogmp category: ${error.message}`);
        console.error("Update error:", error);
      }, 200);
    }
  };

  const technologyOptions =
    technologies?.map((tech) => ({
      value: tech.id,
      label: tech.name
    })) || [];

  const frequencyOptions = [
    { value: "Hourly", label: "Hourly" },
    { value: "Daily", label: "Daily" },
    { value: "Weekly", label: "Weekly" },
    { value: "Monthly", label: "Monthly" },
    { value: "Quarterly", label: "Quarterly" },
    { value: "Yearly", label: "Yearly" }
  ];

  const sampleSizeUnitOptions = [
    { value: "number", label: "Number" },
    { value: "percent", label: "Percentage" }
  ];

  return (
    <Modal show={show} onHide={onModalClose} centered size="lg">
      <Modal.Header closeButton className="d-flex justify-content-center">
        <Modal.Title className="w-100 text-center">{viewOnly ? "View OGMP Category" : "Edit Version OGMP Category"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit} noValidate>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="formCategoryName">
                <Form.Label className="font-weight-bold">
                  <strong>Parent Category</strong>
                </Form.Label>
                <Form.Control type="text" value={parentName || ""} disabled />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="formCategoryName">
                <Form.Label className="font-weight-bold">
                  <strong>Category</strong>
                </Form.Label>
                <Form.Control type="text" value={category?.name || ""} disabled />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              {/* L4 Method - Only enabled if OGMP Level contains "L4" */}
              <Form.Group controlId="formL4Method">
                <Form.Label className="font-weight-bold">
                  <strong>L4 Method</strong>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="l4Method"
                  value={formData.l4Method}
                  onChange={handleChange}
                  disabled={viewOnly}
                  isInvalid={!!validationErrors.l4Method}
                  placeholder={"Enter L4 Method"}
                />
                {validationErrors.l4Method && (
                  <div className="text-danger mt-1 small">
                    {typeof validationErrors.l4Method === "object" ? validationErrors.l4Method.message : validationErrors.l4Method}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="formUncertaintyFraction">
                <Form.Label className="font-weight-bold">
                  <strong>Uncertainty Fraction</strong>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="uncertaintyFraction"
                  value={formData.uncertaintyFraction}
                  onChange={handleNumberChange}
                  placeholder="Enter Uncertainty Fraction"
                  isInvalid={!!validationErrors.uncertaintyFraction}
                  min="0"
                  max="1"
                  step="0.01"
                  disabled={viewOnly}
                />
                {validationErrors.uncertaintyFraction && (
                  <div className="text-danger mt-1 small">
                    {typeof validationErrors.uncertaintyFraction === "object"
                      ? validationErrors.uncertaintyFraction.message
                      : validationErrors.uncertaintyFraction}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="formFrequency">
                <Form.Label className="font-weight-bold">
                  <strong>Frequency</strong> <span className="text-danger">*</span>
                </Form.Label>
                <Select
                  options={frequencyOptions}
                  value={frequencyOptions.find((option) => option.value === formData.frequency) || null}
                  onChange={(option) => handleSelectChange("frequency", option)}
                  placeholder="Select Frequency (required)"
                  isSearchable
                  isDisabled={viewOnly}
                  className={validationErrors.frequency ? "is-invalid" : ""}
                />
                {validationErrors.frequency && (
                  <div className="text-danger mt-1 small">
                    {typeof validationErrors.frequency === "object" ? validationErrors.frequency.message : validationErrors.frequency}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="formMeasurementTechnology">
                <Form.Label className="font-weight-bold">
                  <Container>
                    <strong>Measurement Technology</strong>
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Tooltip id="info-tooltip" className="text-start">
                          You can add new measurement technology in the Measurements tab.
                        </Tooltip>
                      }
                    >
                      <span style={{ cursor: "pointer", marginLeft: "5px" }}>
                        <FontAwesomeIcon icon={faInfoCircle} className="text-primary" style={{ fontSize: "16px" }} />
                      </span>
                    </OverlayTrigger>
                  </Container>
                </Form.Label>
                <Select
                  options={technologyOptions}
                  value={technologyOptions.find((option) => option.value === formData.measurementTechnologyId) || null}
                  onChange={(option) => handleSelectChange("measurementTechnologyId", option)}
                  placeholder="Select Technology"
                  isClearable
                  isSearchable
                  isDisabled={viewOnly}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3" controlId="formDataSource">
                <Form.Label className="font-weight-bold">
                  <strong>Data Source</strong>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="dataSource"
                  value={formData.dataSource}
                  onChange={handleChange}
                  placeholder="Enter data source"
                  disabled={viewOnly}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="formSampleSize">
                <Form.Label className="font-weight-bold">
                  <strong>Sample Size</strong>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="sampleSize"
                  value={formData.sampleSize}
                  onChange={handleNumberChange}
                  placeholder="Enter sample size"
                  isInvalid={!!validationErrors.sampleSize}
                  min="0"
                  max="5000000"
                  step="5"
                  disabled={viewOnly}
                />
                {validationErrors.sampleSize && (
                  <div className="text-danger mt-1 small">
                    {typeof validationErrors.sampleSize === "object" ? validationErrors.sampleSize.message : validationErrors.sampleSize}
                  </div>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="formSampleSizeUnit">
                <Form.Label className="font-weight-bold">
                  <strong>Sample Size Unit</strong> {formData.sampleSize && <span className="text-danger">*</span>}
                </Form.Label>
                <Select
                  options={sampleSizeUnitOptions}
                  value={sampleSizeUnitOptions.find((option) => option.value === formData.sampleSizeUnit) || null}
                  onChange={(option) => handleSelectChange("sampleSizeUnit", option)}
                  placeholder="Select Unit"
                  isClearable
                  isSearchable
                  isDisabled={viewOnly}
                  className={validationErrors.sampleSizeUnit ? "is-invalid" : ""}
                />
                {validationErrors.sampleSizeUnit && (
                  <div className="text-danger mt-1 small">
                    {typeof validationErrors.sampleSizeUnit === "object"
                      ? validationErrors.sampleSizeUnit.message
                      : validationErrors.sampleSizeUnit}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>

          {!viewOnly && (
            <div className="d-grid">
              <Button variant="primary" type="submit" style={{ width: "50%" }} className="mx-auto" disabled={mutationLoading}>
                {mutationLoading ? "Updating Version Category..." : "Update Version Category"}
              </Button>
            </div>
          )}
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export { EditVersionCategoryModal };
